// CampusMeds - Medication Tracking & Refill Reminders
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@features/auth';
import { LoadingSpinner } from '@common/ui';

// Lazy load route components for code splitting
const LoginPage = lazy(() => import('@features/auth').then(m => ({ default: m.LoginPage })));
const LoginForm = lazy(() => import('@features/auth').then(m => ({ default: m.LoginForm })));
const AdminDashboard = lazy(() => import('@features/admin').then(m => ({ default: m.AdminDashboard })));
const AdminMetrics = lazy(() => import('@features/admin/pages/AdminMetrics'));
const KnowledgeBaseList = lazy(() => import('@features/admin/pages/KnowledgeBaseList'));
const KnowledgeBaseEditor = lazy(() => import('@features/admin/pages/KnowledgeBaseEditor'));
const KnowledgeBaseViewer = lazy(() => import('@features/admin/pages/KnowledgeBaseViewer'));
const LandingPage = lazy(() => import('@features/onboarding').then(m => ({ default: m.LandingPage })));
const MedicationForm = lazy(() => import('@features/onboarding').then(m => ({ default: m.MedicationForm })));
const PrivacyPolicy = lazy(() => import('@features/legal').then(m => ({ default: m.PrivacyPolicy })));
const Terms = lazy(() => import('@features/legal/pages/Terms'));
const Contact = lazy(() => import('@features/legal/pages/Contact'));
const About = lazy(() => import('@features/legal/pages/About'));
const HowItWorks = lazy(() => import('@features/legal/pages/HowItWorks'));
const CheckStatus = lazy(() => import('@features/patient/pages/CheckStatus'));
const SmsOptin = lazy(() => import('@features/auth/pages/SmsOptin'));

function App() {
  const { user, loading, isAdmin } = useAuth();

  return (
    <Router>
      <Suspense fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }>
        <Routes>
          {/* Public routes - no auth required */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/form/:email" element={<MedicationForm />} />
          <Route path="/check-status" element={<CheckStatus />} />
          <Route path="/check" element={<CheckStatus />} />
          <Route path="/status" element={<CheckStatus />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/sms-optin" element={<SmsOptin />} />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              user && isAdmin ? <AdminDashboard /> : <Navigate to="/admin" replace />
            }
          />
          <Route
            path="/admin/metrics"
            element={
              user && isAdmin ? <AdminMetrics /> : <Navigate to="/admin" replace />
            }
          />
          <Route
            path="/admin/knowledge-base"
            element={
              user && isAdmin ? <KnowledgeBaseList /> : <Navigate to="/admin" replace />
            }
          />
          <Route
            path="/admin/knowledge-base/new"
            element={
              user && isAdmin ? <KnowledgeBaseEditor /> : <Navigate to="/admin" replace />
            }
          />
          <Route
            path="/admin/knowledge-base/:id"
            element={
              user && isAdmin ? <KnowledgeBaseViewer /> : <Navigate to="/admin" replace />
            }
          />
          <Route
            path="/admin/knowledge-base/:id/edit"
            element={
              user && isAdmin ? <KnowledgeBaseEditor /> : <Navigate to="/admin" replace />
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

// Admin Login Component
function AdminLogin() {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user && isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <LoginForm disableSignup={true} />
    </div>
  );
}

export default App;